import i18nFactory from '@yandex-int/i18n';
import { omit } from 'lodash';
import * as React from 'react';
import Tree, { Extensions, InjectedRendererProps } from 'react-virtualized-tree';

import { Bem } from '../../../service/Bem/Bem';
import { VirtualTreeHelpers } from '../../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import {
    PhrasesSelectorGroupKeywords,
    PhrasesSelectorPhrase,
    PhrasesSelectorStartPhraseFlattenedNode,
    PhrasesSelectorStartPhraseNode,
    PhrasesSelectorTreeUpdateType
} from '../../../typings/phrases';
import { ScreenErrorMessage } from '../../ScreenErrorMessage/ScreenErrorMessage';
import { PhrasesSelectorPhrasesTreeNode } from '../PhrasesSelectorPhrasesTreeNode/PhrasesSelectorPhrasesTreeNode';

import './PhrasesSelectorPhrasesTree.css';
import * as keyset from './PhrasesSelectorPhrasesTree.i18n';

const i18n = i18nFactory(keyset);

export interface PhrasesSelectorPhrasesTreeProps {
    /* @mrronuar при изменениях дерева в обработчик onChange передается полное
               дерево, поэтому нужно всегда иметь исходное
               дерево allPhrases, чтобы при изменениях не потерять узлы. */
    areMinusPhrasesEnabled: boolean;
    allPhrases: Array<PhrasesSelectorStartPhraseNode>;
    phrases: Array<PhrasesSelectorStartPhraseNode>;
    groupKeywords: PhrasesSelectorGroupKeywords;
    errorMessage: string;
    onChange: (nodes: Array<PhrasesSelectorStartPhraseNode>) => void;
    onKeywordAdd: (phrase: PhrasesSelectorPhrase) => void;
    onMinusPhraseAdd: (phrase: string) => void;
    onChildrenFetch: (phrase: PhrasesSelectorStartPhraseNode) => void;
    onMinusWordAdd: (word: string) => void;
}

const BLOCK = 'phrases-selector-phrases-tree';

export class PhrasesSelectorPhrasesTree extends React.PureComponent<PhrasesSelectorPhrasesTreeProps> {
    treeExtensions: Extensions;

    constructor(props: PhrasesSelectorPhrasesTreeProps) {
        super(props);

        this.treeExtensions = {
            updateTypeHandlers: {
                [PhrasesSelectorTreeUpdateType.expand]: this.onExpand,
                [PhrasesSelectorTreeUpdateType.select]: this.onSelect,
                [PhrasesSelectorTreeUpdateType.remove]: this.onRemove,
                [PhrasesSelectorTreeUpdateType.fetchChildren]: this.onChildrenFetch
            }
        };
    }

    render() {
        return <div className={Bem.cls(BLOCK)}>{this.renderContent()}</div>;
    }

    private renderContent = () => {
        const { phrases, errorMessage } = this.props;

        if (errorMessage) {
            return this.renderErrorScreen(errorMessage);
        }

        if (phrases.length === 0) {
            return this.renderEmptyScreen();
        }

        return (
            <Tree nodes={phrases} extensions={this.treeExtensions} onChange={this.onTreeChange}>
                {this.renderNode}
            </Tree>
        );
    };

    private renderNode = ({ style, node, onChange }: InjectedRendererProps<void>) => {
        const { areMinusPhrasesEnabled, groupKeywords, onKeywordAdd, onMinusPhraseAdd, onMinusWordAdd } = this.props;
        const isGroupKeyword = Boolean(groupKeywords[node.name.toLowerCase()]);

        return (
            <div style={omit(style, 'marginLeft', 'cursor', 'userSelect')}>
                <PhrasesSelectorPhrasesTreeNode
                    areMinusPhrasesEnabled={areMinusPhrasesEnabled}
                    isGroupKeyword={isGroupKeyword}
                    node={node as PhrasesSelectorStartPhraseFlattenedNode}
                    onChange={onChange}
                    onKeywordAdd={onKeywordAdd}
                    onMinusPhraseAdd={onMinusPhraseAdd}
                    onMinusWordAdd={onMinusWordAdd}
                />
            </div>
        );
    };

    private renderErrorScreen = (errorMessage: string) => {
        return (
            <div className={Bem.cls(BLOCK, 'status-message-wrapper')}>
                <ScreenErrorMessage
                    cls={Bem.cls(BLOCK, 'status-message')}
                    header={i18n('error-header')}
                    text={errorMessage}
                />
            </div>
        );
    };

    private renderEmptyScreen = () => {
        return (
            <div className={Bem.cls(BLOCK, 'status-message-wrapper')}>
                <ScreenErrorMessage
                    cls={Bem.cls(BLOCK, 'status-message')}
                    header={i18n('empty-header')}
                    text={i18n('empty-text')}
                />
            </div>
        );
    };

    private onTreeChange = (nodes: Array<PhrasesSelectorStartPhraseNode>) => {
        this.props.onChange(VirtualTreeHelpers.selectParents(nodes));
    };

    private onExpand = (nodes: Array<PhrasesSelectorStartPhraseNode>, updatedNode: PhrasesSelectorStartPhraseNode) => {
        return VirtualTreeHelpers.replaceNode(this.props.allPhrases, updatedNode);
    };

    private onSelect = (nodes: Array<PhrasesSelectorStartPhraseNode>, updatedNode: PhrasesSelectorStartPhraseNode) => {
        return VirtualTreeHelpers.selectNodes(this.props.allPhrases, [updatedNode]);
    };

    private onRemove = (nodes: Array<PhrasesSelectorStartPhraseNode>, removedNode: PhrasesSelectorStartPhraseNode) => {
        return VirtualTreeHelpers.removeNodes(this.props.allPhrases, node => node.id === removedNode.id);
    };

    private onChildrenFetch = (
        nodes: Array<PhrasesSelectorStartPhraseNode>,
        updatedNode: PhrasesSelectorStartPhraseNode
    ) => {
        const { allPhrases, onChildrenFetch } = this.props;

        onChildrenFetch(updatedNode);

        return VirtualTreeHelpers.replaceNode(allPhrases, updatedNode);
    };
}
